/**
 * API client — ported from Studio, using workspace config/store
 */

import { CONFIG } from '../core/config.js'
import { store } from '../core/store.js'
import { eventBus, EVENTS } from '../core/event-bus.js'
import { CREATOR_HUB_CANONICAL_CLIENT_ID } from '../modules/auth/canonical-client.js'

/**
 * H-147: an auth-gated action (upload/presign/generate/favorite) must never
 * die as a silent 401 — toast and open the login modal (which pre-fills the
 * Shopify email hint when the storefront provided one).
 */
export function handleAuthRequired(status) {
  if (status !== 401) return
  eventBus.emit(EVENTS.TOAST_SHOW, { type: 'info', message: 'Please sign in to create' })
  store.setState('auth.showLoginModal', true)
}

// Generate or get session ID for anonymous uploads
export function getSessionId() {
  // Migration shim: carry over existing session from old key name
  let sessionId = localStorage.getItem('mms_session_id') || localStorage.getItem('hex_session_id')
  if (!sessionId) {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
    sessionId = 'sess_'
    for (let i = 0; i < 24; i++) {
      sessionId += chars.charAt(Math.floor(Math.random() * chars.length))
    }
  }
  localStorage.setItem('mms_session_id', sessionId)
  return sessionId
}

export const REQUEST_TIMEOUT_MS = Object.freeze({
  DEFAULT: 15_000,
  WRITE: 30_000,
  ANALYSIS: 60_000,
  GENERATION: 120_000,
  UPLOAD: 300_000,
})

/**
 * Bound browser requests so a stalled worker cannot leave the workspace pending forever.
 */
export async function fetchWithTimeout(url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS.DEFAULT) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  const signal = options.signal
  const abortFromCaller = () => controller.abort(signal.reason)
  if (signal?.aborted) {
    controller.abort(signal.reason)
  } else {
    signal?.addEventListener('abort', abortFromCaller, { once: true })
  }

  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abortFromCaller)
  }
}

// Unified API request function with auth header injection
export async function apiRequest(url, options = {}) {
  const { timeoutMs = REQUEST_TIMEOUT_MS.DEFAULT, ...fetchOptions } = options
  const headers = { ...(fetchOptions.headers || {}) }
  const token = store.getState('auth.token')

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  } else {
    headers['X-Session-ID'] = getSessionId()
  }

  const response = await fetchWithTimeout(url, { ...fetchOptions, headers }, timeoutMs)

  // Guard: skip .json() on empty body (204, Cloudflare error pages, etc.)
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      handleAuthRequired(response.status)
      throw apiError(`API request failed: ${response.status}`, response.status)
    }
    return {}
  }
  const text = await response.text()
  if (!text) {
    handleAuthRequired(response.status)
    throw apiError(`API request failed: ${response.status} (empty response)`, response.status)
  }
  const data = JSON.parse(text)

  if (!response.ok || (!data.success && data.code !== 200)) {
    handleAuthRequired(response.status)
    // Platform-envelope errors are objects ({code, message}); legacy routes
    // send strings. Normalize so err.message is never "[object Object]" and
    // callers can branch on err.status/err.code instead of prose matching.
    const msg =
      (typeof data.error === 'string' && data.error) ||
      data.error?.message ||
      data.msg ||
      `API request failed: ${response.status}`
    throw apiError(msg, response.status, data.error?.code)
  }

  return data
}

// Error carrying the HTTP status + envelope code so callers can branch on
// machine-readable fields (e.g. 401 → login retry) instead of message prose.
function apiError(message, status, code = null) {
  const err = new Error(message)
  err.status = status
  err.code = code || null
  return err
}

// Two-step upload to create-runtime: presign -> PUT bytes to R2 -> finalize.
// Returns the asset row ({ id, public_url, ... }); asset.id becomes the
// reference_asset_id for edit/apparel generate intents. Replaces the retired
// single-step POST /api/images. why → ADR-005 #2 / SKILL.md §Endpoint Contract.
export async function uploadImage(formData, onProgress) {
  const file = formData.get('image') || formData.get('file')
  if (!file) throw new Error('No image file to upload')
  const format = (file.type?.split('/')[1] || 'png').toLowerCase()
  const size_bytes = file.size

  const token = store.getState('auth.token')
  const authHeaders = token
    ? { Authorization: 'Bearer ' + token }
    : { 'X-Session-ID': getSessionId() }
  const jsonHeaders = { 'Content-Type': 'application/json', ...authHeaders }

  // 1) presign — get an R2 signed PUT URL
  const presignRes = await fetchWithTimeout(CONFIG.FRONTEND_API + '/api/customer/assets/presign', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ type: 'image', format, size_bytes }),
  })
  if (!presignRes.ok) {
    handleAuthRequired(presignRes.status)
    throw new Error(`Upload presign failed: ${presignRes.status}`)
  }
  const { r2_key, upload_url } = (await presignRes.json()).data || {}
  if (!r2_key || !upload_url) throw new Error('Presign returned no upload URL')

  // 2) PUT raw bytes straight to R2 (not through the worker)
  const putRes = await fetchWithTimeout(
    upload_url,
    {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/png' },
      body: file,
    },
    REQUEST_TIMEOUT_MS.UPLOAD
  )
  if (!putRes.ok) throw new Error(`Upload to storage failed: ${putRes.status}`)
  if (onProgress) onProgress(100)

  // 2.5) client-side SHA-256 for the dedup fast-path (server re-hashes the
  // bytes anyway — this just lets finalize skip the re-key on known content)
  let sha256
  try {
    const buf = await file.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buf)
    sha256 = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    sha256 = undefined
  }

  // 3) finalize — register the asset row, returns it (with id + public_url)
  const artist = (() => {
    try {
      const customer = JSON.parse(localStorage.getItem('mms_customer') || '{}')
      return customer.username || customer.email?.split('@')[0] || 'user'
    } catch {
      return 'user'
    }
  })()
  const finalizeRes = await fetchWithTimeout(
    CONFIG.FRONTEND_API + '/api/customer/assets/finalize',
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ r2_key, type: 'image', format, size_bytes, artist, sha256 }),
    }
  )
  if (!finalizeRes.ok) {
    handleAuthRequired(finalizeRes.status)
    throw new Error(`Upload finalize failed: ${finalizeRes.status}`)
  }
  return (await finalizeRes.json()).data
}

// Fetch image detail (includes AI metadata)
export async function fetchImageDetail(imageId) {
  const token = store.getState('auth.token')
  const headers = {}
  if (token) {
    headers['Authorization'] = 'Bearer ' + token
  } else {
    headers['X-Session-ID'] = getSessionId()
  }

  const response = await fetchWithTimeout(`${CONFIG.FRONTEND_API}/api/images/${imageId}`, {
    headers,
  })
  const data = await response.json()

  if (data.success || data.code === 200) {
    return data.data
  }
  throw new Error(data.error || data.msg || 'Failed to load image detail')
}

// Auth APIs — canonical customer realm only (requirements-v1 §3 Option A,
// H-2026-10-16-175). The legacy /api/auth/* chain is retired (404 in
// production); every login surface consolidates on /api/v1/auth.
// H-180: raw bearer validation at the embed edge is gone with the raw-token
// handoff — cross-surface sessions arrive as one-time handoff codes and
// redeem through modules/auth/canonical-client.js.

// Envelope errors arrive as { success:false, error:{ code, message } } — an
// object thrown raw renders "[object Object]" in toasts.
function envelopeErrorMessage(data, fallback) {
  const err = data?.error
  if (typeof err === 'string') return err
  if (err?.message) return err.message
  return fallback
}

// Canonical password login: { email, password, client_id } -> token pair +
// customer ({ token, refresh_token, expires_at, customer }).
export async function loginPassword(email, password) {
  const response = await fetchWithTimeout(`${CONFIG.CUSTOMER_API}/api/v1/auth/login/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, client_id: CREATOR_HUB_CANONICAL_CLIENT_ID }),
  })
  const data = await response.json()
  if (data.success && data.data) return data.data
  throw new Error(envelopeErrorMessage(data, 'Login failed'))
}

// H-174 contract (parallel backend card): POST /signup/password
// { email, password } -> token pair symmetric with /login/password. A 409
// envelope means the email is already registered — surface it verbatim.
export async function signupPassword(email, password) {
  const response = await fetchWithTimeout(`${CONFIG.CUSTOMER_API}/api/v1/auth/signup/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, client_id: CREATOR_HUB_CANONICAL_CLIENT_ID }),
  })
  const data = await response.json()
  if (data.success && data.data) return data.data
  throw new Error(envelopeErrorMessage(data, 'Registration failed'))
}

// H-147 email_code provider — canonical customer realm endpoints. Server
// error messages are surfaced verbatim (C-1 pattern: never fake a state).
export async function beginEmailCode(email) {
  const response = await fetchWithTimeout(`${CONFIG.CUSTOMER_API}/api/v1/auth/email-code/begin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  const data = await response.json()
  if (data.success && data.data) return data.data
  throw new Error(envelopeErrorMessage(data, 'Could not send the sign-in code'))
}

export async function verifyEmailCode(email, code) {
  const response = await fetchWithTimeout(`${CONFIG.CUSTOMER_API}/api/v1/auth/email-code/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, client_id: CREATOR_HUB_CANONICAL_CLIENT_ID }),
  })
  const data = await response.json()
  if (data.success && data.data) return data.data
  throw new Error(envelopeErrorMessage(data, 'Sign-in failed'))
}
